import type { ParticipantRole, RoomKind, TurnCredentials } from '../realtime/frames';
import { parseRoomKind, parseTurnCredentials } from '../realtime/frames';

// The TURN wire shape and its narrowing parser live with the rest of the protocol parsing
// (realtime/frames.ts, also used by the lobby_admitted frame); re-exported here so the join
// API keeps its historical surface.
export { parseTurnCredentials };
export type { TurnCredentials };

/** Guest join body: the validated display name, plus the optional invitation secret (CR-20). */
export interface GuestJoinBody {
  readonly displayName: string;
  /**
   * The invitation secret of a deep link `/r/<slug>?invite=<token>` (CR-20): a live invitation
   * auto-admits a join that would otherwise wait in the lobby. Never sent on the SSO path (the
   * contract keeps the two body alternatives disjoint); any failure shape falls back to the
   * plain guest path server-side and surfaces as `inviteRejected` on the response.
   */
  readonly inviteToken?: string;
}

/** SSO join body: the Keycloak access token; mutually exclusive with the guest fields. */
export interface SsoJoinBody {
  readonly ssoToken: string;
}

/** The two disjoint join body shapes of POST /api/v1/rooms/{room}/join (docs/contract.md M5). */
export type JoinRequestBody = GuestJoinBody | SsoJoinBody;

/** Credentials returned by POST /api/v1/rooms/{room}/join, docs/contract.md (M0.3 amendment). */
export interface JoinCredentials {
  readonly token: string;
  readonly url: string;
  readonly expiresAt: string;
  /**
   * Server-assigned participant identity and the authoritative value for the realtime presence:
   * `guest-<16 hex>` for guests (the join body no longer carries an identity), `kc-<sub>` for
   * SSO joins. Required: the control service always resolves one.
   */
  readonly identity: string;
  /**
   * Resolved stand-model role of the joiner. Absent or unrecognized values clamp to
   * 'participant'; the WS state frame remains authoritative after the join.
   */
  readonly role: ParticipantRole;
  /**
   * The room's kind (CR-12): the authoritative layout signal the SPA derives its
   * meeting/webinar surfaces from. Absent or unrecognized values clamp to 'meeting';
   * the WS state frame remains authoritative after the join.
   */
  readonly kind: RoomKind;
  /**
   * The room's lobby option (CR-14): lets the SPA know before the WS handshake whether the join
   * will be held in the lobby (`lobby_wait`) or pass straight in. Absent on pre-CR-14 servers;
   * unrecognized values clamp to false - the WS frames remain authoritative either way.
   */
  readonly lobbyRequired: boolean;
  /**
   * The deferred lobby shape (CR-15): true when the join was held and the response carries NO
   * usable media credentials - the token authenticates the control WS only (the SFU refuses
   * it) and the turn section is withheld. The real credentials arrive over the WS as the
   * `lobby_admitted` frame when a staff member admits the join. Undefined/false on direct
   * joins; the field is the single authoritative hold signal - never infer it from
   * lobbyRequired+role, the bypass cases answer the full shape with the same role.
   */
  readonly mediaDeferred?: boolean | undefined;
  /**
   * Present only when a live invitation admitted this join past the lobby (CR-20). Absent on
   * every other join - including joins that never needed the invitation (open room, staff).
   */
  readonly inviteAccepted?: true | undefined;
  /**
   * Present only when a presented `inviteToken` was NOT accepted (CR-20): the value is the
   * server's one generic rejection text regardless of the failure shape (anti-enumeration).
   * The join itself still succeeds on the plain guest path - the lobby still applies.
   */
  readonly inviteRejected?: string | undefined;
  /**
   * Present only when the control service mints TURN credentials. Stands without TURN omit the
   * section; a section that violates its shape is treated as absent, never as a join failure.
   * `| undefined` is explicit for exactOptionalPropertyTypes.
   */
  readonly turn?: TurnCredentials | undefined;
}

/** Failure of the join endpoint: HTTP error, network failure or malformed response body. */
export class JoinApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'JoinApiError';
    this.status = status;
    this.code = code;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Narrows an unknown `role` field to the participant-role vocabulary; absence and unrecognized
 * values clamp to 'participant', mirroring the server's own roleFromStored mapping.
 */
function parseRole(value: unknown): ParticipantRole {
  return value === 'admin' || value === 'moderator' || value === 'participant'
    ? value
    : 'participant';
}

/**
 * Narrows an unknown join response body to {@link JoinCredentials}. Throws a TypeError naming the
 * first field that violates the contract shape. token, url, expiresAt and identity are required
 * non-empty strings (the server-assigned identity is the authoritative participant identity; on
 * a deferred join the token is the grant-less lobby shape - still required, still non-empty);
 * role carries the wire vocabulary when present and clamps to 'participant' otherwise.
 */
export function parseJoinResponse(body: unknown): JoinCredentials {
  if (!isRecord(body)) {
    throw new TypeError('join response must be a JSON object');
  }
  if (typeof body.token !== 'string' || body.token.length === 0) {
    throw new TypeError('join response field "token" must be a non-empty string');
  }
  if (typeof body.url !== 'string' || body.url.length === 0) {
    throw new TypeError('join response field "url" must be a non-empty string');
  }
  if (typeof body.expiresAt !== 'string' || body.expiresAt.length === 0) {
    throw new TypeError('join response field "expiresAt" must be a non-empty string');
  }
  if (typeof body.identity !== 'string' || body.identity.length === 0) {
    throw new TypeError('join response field "identity" must be a non-empty string');
  }
  return {
    token: body.token,
    url: body.url,
    expiresAt: body.expiresAt,
    identity: body.identity,
    role: parseRole(body.role),
    kind: parseRoomKind(body.kind),
    lobbyRequired: body.lobbyRequired === true,
    // The deferred shape is only ever present when true (the server omits the field on direct
    // joins); keeping the key absent preserves the parsed shape of a direct join verbatim.
    ...(body.mediaDeferred === true ? { mediaDeferred: true } : {}),
    // The invitation outcomes (CR-20) follow the same present-only-when-meaningful rule.
    ...(body.inviteAccepted === true ? { inviteAccepted: true as const } : {}),
    ...(typeof body.inviteRejected === 'string' && body.inviteRejected.length > 0
      ? { inviteRejected: body.inviteRejected }
      : {}),
    turn: parseTurnCredentials(body.turn),
  };
}

function isSsoJoinBody(body: JoinRequestBody): body is SsoJoinBody {
  return typeof (body as Partial<SsoJoinBody>).ssoToken === 'string';
}

/**
 * Mints join credentials from the control service. Same-origin call: the Vite dev server proxies
 * /api to the control service (CONTROL_ORIGIN), production serves the SPA same-origin. The body
 * is one of the two disjoint contract shapes: the guest displayName (the identity is
 * server-assigned and returned in the response) or the SSO access token (never a mix - the
 * server answers invalid_request to one).
 */
export async function joinRoom(room: string, request: JoinRequestBody): Promise<JoinCredentials> {
  const payload = isSsoJoinBody(request) ? { ssoToken: request.ssoToken } : request;
  let response: Response;
  try {
    response = await fetch(`/api/v1/rooms/${encodeURIComponent(room)}/join`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    throw new JoinApiError(0, 'network_error', 'control service is unreachable');
  }

  if (!response.ok) {
    throw await errorFromResponse(response, 'join');
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new JoinApiError(response.status, 'invalid_response', 'join response is not valid JSON');
  }

  try {
    return parseJoinResponse(body);
  } catch (cause: unknown) {
    const detail = cause instanceof TypeError ? cause.message : 'malformed join response';
    throw new JoinApiError(response.status, 'invalid_response', detail);
  }
}

/**
 * Renews a join token through POST /api/v1/rooms/{room}/remint. No request body: the STILL VALID
 * join JWT authenticates the call (Bearer header) and supplies room, identity and display name.
 * The response is the join shape for the SAME identity - a guest whose token expires mid-room
 * renews in place instead of re-joining, which would mint a new server-assigned identity and
 * duplicate the participant in presence, hand state and chat authorship. Parsing reuses the join
 * response parser: identity stays REQUIRED, because a remint must echo the old identity.
 */
export async function remintToken(room: string, token: string): Promise<JoinCredentials> {
  let response: Response;
  try {
    response = await fetch(`/api/v1/rooms/${encodeURIComponent(room)}/remint`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
  } catch {
    throw new JoinApiError(0, 'network_error', 'control service is unreachable');
  }

  if (!response.ok) {
    throw await errorFromResponse(response, 'remint');
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new JoinApiError(
      response.status,
      'invalid_response',
      'remint response is not valid JSON',
    );
  }

  try {
    return parseJoinResponse(body);
  } catch (cause: unknown) {
    const detail = cause instanceof TypeError ? cause.message : 'malformed remint response';
    throw new JoinApiError(response.status, 'invalid_response', detail);
  }
}

/** Builds the JoinApiError for a non-2xx response, using the contract's {"error":{code,message}} body when present. */
async function errorFromResponse(response: Response, endpoint: string): Promise<JoinApiError> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // non-JSON error body, keep the generic message
  }

  let code = 'unknown_error';
  let message = `${endpoint} request failed with HTTP ${response.status}`;
  if (isRecord(body) && isRecord(body.error)) {
    if (typeof body.error.code === 'string') {
      code = body.error.code;
    }
    if (typeof body.error.message === 'string') {
      message = body.error.message;
    }
  }
  return new JoinApiError(response.status, code, message);
}
